import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { MDRBadge } from "@/components/MDRBadge";
import { CsvIOButtons } from "@/components/admin/CsvIOButtons";
import { toast } from "sonner";
import {
  managersData, pharmaciesData, accreditedStructuresData
} from "@/data/platformData";
import { Users, Plus, Pencil, Trash2, Mail, Phone, Building2, Hospital } from "lucide-react";

const ROLE_LABELS = { pharmacy_manager: "Resp. Farmacia", structure_manager: "Resp. Struttura" };
const ROLE_STYLE = {
  pharmacy_manager: "bg-indigo-50 text-indigo-700 border-indigo-200",
  structure_manager: "bg-cyan-50 text-cyan-700 border-cyan-200"
};

const empty = { name: "", email: "", phone: "", role: "pharmacy_manager", pharmacyIds: [], structureIds: [] };

const csvColumns = [
  { key: "id", label: "id" },
  { key: "name", label: "nome" },
  { key: "email", label: "email" },
  { key: "phone", label: "telefono" },
  { key: "role", label: "ruolo" },
  { key: "pharmacyIds", label: "farmacie", get: (r) => (r.pharmacyIds || []).join("|"), parse: (v) => v.split("|").map(s => s.trim()).filter(Boolean) },
  { key: "structureIds", label: "strutture", get: (r) => (r.structureIds || []).join("|"), parse: (v) => v.split("|").map(s => s.trim()).filter(Boolean) }
];

export const AdminUsersPage = () => {
  const [items, setItems] = useState(managersData);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);

  const startEdit = (it) => { setEditing(it ? { ...it, pharmacyIds: [...(it.pharmacyIds || [])], structureIds: [...(it.structureIds || [])] } : { ...empty, id: `mgr${Date.now()}` }); setOpen(true); };
  const save = () => {
    if (!editing.name) { toast.error("Inserire nome"); return; }
    setItems(prev => {
      const exists = prev.find(p => p.id === editing.id);
      return exists ? prev.map(p => p.id === editing.id ? editing : p) : [...prev, editing];
    });
    setOpen(false);
    toast.success("Responsabile salvato");
  };
  const remove = (id) => { setItems(prev => prev.filter(p => p.id !== id)); toast.success("Responsabile rimosso"); };

  const toggleAssoc = (key, id) => {
    setEditing(e => {
      const cur = e[key] || [];
      return { ...e, [key]: cur.includes(id) ? cur.filter(x => x !== id) : [...cur, id] };
    });
  };

  return (
    <div className="p-4 md:p-8 max-w-5xl mx-auto space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Users className="h-4 w-4 text-slate-600" />
            <span className="text-xs uppercase tracking-wider text-muted-foreground">4 · Responsabili</span>
            <MDRBadge variant="admin" size="sm" />
          </div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight">Censimento responsabili</h1>
          <p className="text-sm text-muted-foreground mt-1">{items.length} responsabili · associati a farmacie e strutture</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <CsvIOButtons rows={items} columns={csvColumns} filename="responsabili" onImport={(p) => setItems(p.map(x => ({ ...empty, ...x, pharmacyIds: Array.isArray(x.pharmacyIds) ? x.pharmacyIds : [], structureIds: Array.isArray(x.structureIds) ? x.structureIds : [] })))} testIdPrefix="users-csv" />
          <Button variant="premium" onClick={() => startEdit(null)} className="gap-2" data-testid="users-new-btn">
            <Plus className="h-4 w-4" /> Nuovo responsabile
          </Button>
        </div>
      </div>

      <div className="grid gap-3 md:grid-cols-2">
        {items.map(m => (
          <Card key={m.id} data-testid={`user-card-${m.id}`}>
            <CardHeader className="pb-2">
              <div className="flex items-start justify-between gap-2">
                <CardTitle className="text-base">{m.name}</CardTitle>
                <Badge variant="outline" className={ROLE_STYLE[m.role]}>{ROLE_LABELS[m.role]}</Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="space-y-1 text-xs text-muted-foreground">
                <span className="inline-flex items-center gap-1"><Mail className="h-3 w-3" /> {m.email}</span>
                <span className="inline-flex items-center gap-1 ml-3"><Phone className="h-3 w-3" /> {m.phone}</span>
              </div>
              {m.role === "pharmacy_manager" && (
                <div className="flex flex-wrap gap-1 pt-1">
                  {(m.pharmacyIds || []).map(pid => {
                    const ph = pharmaciesData.find(p => p.id === pid);
                    return ph ? <Badge key={pid} variant="secondary" className="gap-1 text-[10px]"><Building2 className="h-3 w-3" />{ph.name}</Badge> : null;
                  })}
                </div>
              )}
              {m.role === "structure_manager" && (
                <div className="flex flex-wrap gap-1 pt-1">
                  {(m.structureIds || []).map(sid => {
                    const s = accreditedStructuresData.find(x => x.id === sid);
                    return s ? <Badge key={sid} variant="secondary" className="gap-1 text-[10px]"><Hospital className="h-3 w-3" />{s.name}</Badge> : null;
                  })}
                </div>
              )}
              <div className="flex justify-end gap-1 pt-2 border-t">
                <Button variant="ghost" size="icon" onClick={() => startEdit(m)} data-testid={`user-edit-${m.id}`}><Pencil className="h-4 w-4" /></Button>
                <Button variant="ghost" size="icon" className="text-destructive" onClick={() => remove(m.id)}><Trash2 className="h-4 w-4" /></Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing && items.find(p => p.id === editing.id) ? "Modifica responsabile" : "Nuovo responsabile"}</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div><Label>Nome</Label><Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="user-form-name" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Email</Label><Input type="email" value={editing.email} onChange={(e) => setEditing({ ...editing, email: e.target.value })} /></div>
                <div><Label>Telefono</Label><Input value={editing.phone} onChange={(e) => setEditing({ ...editing, phone: e.target.value })} /></div>
              </div>
              <div>
                <Label>Ruolo</Label>
                <select className="w-full mt-1 border rounded-md p-2 text-sm bg-background"
                  value={editing.role}
                  onChange={(e) => setEditing({ ...editing, role: e.target.value })}>
                  <option value="pharmacy_manager">Responsabile Farmacia</option>
                  <option value="structure_manager">Responsabile Struttura</option>
                </select>
              </div>
              {editing.role === "pharmacy_manager" && (
                <div>
                  <Label>Farmacie associate</Label>
                  <div className="grid grid-cols-1 gap-1 mt-1 p-2 rounded-md border max-h-40 overflow-y-auto">
                    {pharmaciesData.map(ph => (
                      <label key={ph.id} className="flex items-center gap-2 text-sm cursor-pointer">
                        <Checkbox checked={(editing.pharmacyIds || []).includes(ph.id)} onCheckedChange={() => toggleAssoc("pharmacyIds", ph.id)} data-testid={`user-pharmacy-${ph.id}`} />
                        <span className="truncate">{ph.name}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
              {editing.role === "structure_manager" && (
                <div>
                  <Label>Strutture associate</Label>
                  <div className="grid grid-cols-1 gap-1 mt-1 p-2 rounded-md border max-h-40 overflow-y-auto">
                    {accreditedStructuresData.map(s => (
                      <label key={s.id} className="flex items-center gap-2 text-sm cursor-pointer">
                        <Checkbox checked={(editing.structureIds || []).includes(s.id)} onCheckedChange={() => toggleAssoc("structureIds", s.id)} data-testid={`user-structure-${s.id}`} />
                        <span className="truncate">{s.name}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
            <Button onClick={save} data-testid="user-form-save">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminUsersPage;
